import React, { useState, useRef } from "react";
import {
  Box,
  Grid,
  IconButton,
  Fab,
  InputAdornment,
  TextField,
  MenuItem,
  useMediaQuery,
  useTheme
} from "@mui/material";
import {
  ErrorOutline,
  FilterList,
  SearchOff,
  Add,
  Search,
  Sort
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import PostGrid from "../../components/posts/PostGrid";
import FilterSidebar from "../../components/filters/FilterSidebar";
import FilterDrawer from "../../components/filters/FilterDrawer";
import EmptyState from "../../components/shared/EmptyState/EmptyState";
import { usePosts } from "../../hooks/usePosts";
import Loader from "../../components/loader/Loader";
import PostSkeleton from "../../components/posts/PostSkeleton";

const PostsPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [filterOpen, setFilterOpen] = useState(false);

  const myPost = useSelector(state => state.post.myPost);
  const postsScrollRef = useRef(null);

  const {
    posts,
    loading,
    loadingMore,
    error,
    isEmpty,
    handleApplyFilters,
    handleClearFilters,
    searchQuery,
    setSearchQuery,
    sortBy,
    setSortBy,
  } = usePosts({ scrollContainerRef: postsScrollRef });

  const toggleFilters = () => setFilterOpen(!filterOpen);

  return (
    <>
      {loading && <Loader />}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: { xs: "calc(100vh - 56px)", md: "calc(100vh - 64px)" },
          overflow: "hidden",
          boxSizing: "border-box",
          p: { xs: 1.5, sm: 2, md: 3 },
        }}
      >
        {/* Search and Filters Section — does not scroll */}
        <Box
          sx={{
            mb: 3,
            flex: "0 0 auto",
            display: "flex",
            gap: 2,
            flexDirection: "row",
            alignItems: "stretch"
          }}
        >
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search listings..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              "& .MuiInputBase-input": { color: "#212121" },
              "& .MuiInputBase-input::placeholder": { color: "#888", opacity: 1 },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: "#555" }} />
                </InputAdornment>
              ),
              sx: {
                borderRadius: "50px",
                backgroundColor: "#fff",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderWidth: "2px",
                  borderColor: "#001d3d",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#001d3d",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#001d3d",
                },
              },
            }}
          />

          {!isMobile && (
            <TextField
              select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              variant="outlined"
              sx={{
                minWidth: 180,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "50px",
                  backgroundColor: "#001d3d",
                  color: "#fff",
                  "& .MuiSelect-icon": { color: "#fff" },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#001d3d",
                  },
                  "&:hover": {
                    backgroundColor: "#001d3d",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#001d3d",
                    },
                  },
                  "&.Mui-focused": {
                    backgroundColor: "#001d3d !important",
                    background: "#001d3d !important",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#001d3d",
                    },
                  },
                },
                "& .MuiSelect-select": { color: "#fff" },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Sort fontSize="small" sx={{ color: "#fff" }} />
                  </InputAdornment>
                ),
              }}
              SelectProps={{
                MenuProps: {
                  anchorOrigin: { vertical: "bottom", horizontal: "left" },
                  transformOrigin: { vertical: "top", horizontal: "left" },
                  PaperProps: {
                    sx: {
                      backgroundColor: "#fff",
                      color: "#212121",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                      borderRadius: "8px",
                      border: "1px solid #e0e0e0",
                      mt: 0.5,
                      maxHeight: "180px",
                      overflowY: "auto",
                      "& .MuiList-root": { py: 0.5 },
                      "& .MuiMenuItem-root": {
                        fontSize: "0.875rem",
                        color: "#212121",
                        py: 0.75,
                        "&:hover": { backgroundColor: "#f5f5f5" },
                        "&.Mui-selected": {
                          backgroundColor: "#001d3d",
                          color: "#fff",
                          "&:hover": { backgroundColor: "#002d5a" },
                        },
                      },
                    },
                  },
                },
              }}
            >
              <MenuItem value="newest">Newest First</MenuItem>
              <MenuItem value="priceLow">Price: Low to High</MenuItem>
              <MenuItem value="priceHigh">Price: High to Low</MenuItem>
              <MenuItem value="recentlyUpdated">Recently Updated</MenuItem>
            </TextField>
          )}

          {isMobile && (
            <IconButton
              onClick={toggleFilters}
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                alignSelf: "flex-start",
                height: 56,
                width: 56,
              }}
            >
              <FilterList />
            </IconButton>
          )}
        </Box>

        {/* Filter Drawer for Mobile */}
        {isMobile && (
          <FilterDrawer
            open={filterOpen}
            onClose={toggleFilters}
            onApply={handleApplyFilters}
            onClear={handleClearFilters}
          />
        )}

        {/* Content area — flex: 1, only posts column scrolls */}
        <Box sx={{ flex: 1, overflow: "hidden", display: "flex", gap: { md: 2, lg: 3 } }}>
          {/* Sidebar Filters for Desktop — does not scroll */}
          {!isMobile && (
            <Box sx={{
              width: { md: "28%", lg: "24%", xl: "22%" },
              flexShrink: 0,
              overflowY: "auto",
              scrollbarWidth: "thin",
              scrollbarColor: "#d0d0d0 transparent",
              "&::-webkit-scrollbar": { width: "4px" },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": { background: "#d0d0d0", borderRadius: "4px" },
            }}>
              <FilterSidebar
                onApply={handleApplyFilters}
                onClear={handleClearFilters}
              />
            </Box>
          )}

          {/* Posts Grid — only this scrolls (ref for infinite scroll) */}
          <Box
            ref={postsScrollRef}
            sx={{
            flex: 1,
            overflowY: "auto",
            scrollbarWidth: "thin",
            scrollbarColor: "#d0d0d0 transparent",
            "&::-webkit-scrollbar": { width: "4px" },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": { background: "#d0d0d0", borderRadius: "4px" },
            pr: 0.5,
          }}>
            {error === true && (
              <EmptyState
                title="Error loading posts"
                description="Please try again later"
                icon={<ErrorOutline color="error" fontSize="large" />}
              />
            )}

            {isEmpty && (
              <EmptyState
                title="No posts found"
                description="Try adjusting your filters"
                icon={<SearchOff color="disabled" fontSize="large" />}
                actionText="Clear Filters"
                onAction={handleClearFilters}
              />
            )}

            {!isEmpty && !error && (
              <>
                <PostGrid posts={posts} loading={loading} />
                {loadingMore && (
                  <Grid container spacing={2} className="loading-more" sx={{ mt: 3 }}>
                    <Grid item xs={12} sm={6} md={4}><PostSkeleton /></Grid>
                    <Grid item xs={12} sm={6} md={4}><PostSkeleton /></Grid>
                    <Grid item xs={12} sm={6} md={4}><PostSkeleton /></Grid>
                  </Grid>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* Floating Action Button */}
      {(!myPost || !myPost._id) && (
        <Fab
          color="primary"
          aria-label="add"
          onClick={() => navigate("/fmr/create-post")}
          sx={{
            height: 56,
            width: 56,
            bottom: 32,
            top: "auto !important",
            right: 32,
            position: "fixed",
            backgroundColor: theme.palette.primary.main,
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
              transform: "scale(1.1)"
            },
            transition: "all 0.3s ease"
          }}
        >
          <Add />
        </Fab>
      )}
    </>
  );
};

export default PostsPage;
